/**
 * Audit PP2 — Paper-Trading-Konto-Übersicht.
 *
 * Verdichtet alle Konto-Kennzahlen aus computeAccountSummary in eine
 * cockpit-artige Card. Wird oben auf der Positionen-Seite gerendert
 * (alternativ auch im Dashboard nutzbar).
 */

import { Wallet, TrendingUp, TrendingDown, Activity } from 'lucide-react';

import type { AccountSummary } from '@/lib/paper-account';
import { cn } from '@/lib/utils';
import { CARD_PROMINENT, SECTION_DIVIDER } from '@/lib/design-tokens';

interface Props {
  summary: AccountSummary;
  /** Diät 14.07. (#39): Dashboard zeigt nur die 3 Kern-Zahlen — die volle
   *  Statistik (Win-Rate, Profit-Faktor, …) lebt im Depot, wo sie hingehört;
   *  bei n=1 Trades sind 14 Kennzahlen nur Rauschen. */
  compact?: boolean;
}

function fmtMoney(v: number, ccy: string): string {
  if (!Number.isFinite(v)) return '–';
  const symbol = ccy.toUpperCase() === 'EUR' ? '€' : ccy.toUpperCase();
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  const abs = Math.abs(Math.round(v));
  return `${sign}${abs.toLocaleString('de-DE')} ${symbol}`;
}

function fmtMoneyPlain(v: number, ccy: string): string {
  if (!Number.isFinite(v)) return '–';
  const symbol = ccy.toUpperCase() === 'EUR' ? '€' : ccy.toUpperCase();
  return `${Math.round(v).toLocaleString('de-DE')} ${symbol}`;
}

function fmtPct(v: number | null, digits = 1): string {
  if (v === null || !Number.isFinite(v)) return '–';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(digits)} %`;
}

const ASSESSMENT_PILL = {
  good: 'bg-emerald-900/40 border-emerald-700/60 text-emerald-200',
  neutral: 'bg-zinc-800/60 border-zinc-700/60 text-zinc-200',
  warn: 'bg-red-900/40 border-red-700/60 text-red-200',
} as const;

export function AccountOverviewPanel({ summary, compact = false }: Props) {
  const ccy = summary.account_currency;
  const pnlGood = summary.total_pnl >= 0;

  if (compact) {
    return (
      <section className={cn(CARD_PROMINENT, 'space-y-3')}>
        <header className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Wallet className="size-4 text-blue-300" />
            <h2 className="text-sm font-semibold text-zinc-100">Paper-Konto</h2>
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">
              {summary.open_count} offen · {summary.closed_count} geschlossen
            </span>
          </div>
          <span
            className={cn(
              'rounded border px-2 py-0.5 text-[11px] font-medium',
              ASSESSMENT_PILL[summary.assessment.tone]
            )}
          >
            {summary.assessment.headline}
          </span>
        </header>
        <div className="grid grid-cols-3 gap-3">
          <Big label="Equity" value={fmtMoneyPlain(summary.equity, ccy)} />
          <Big
            label="Gesamt P&L"
            value={fmtMoney(summary.total_pnl, ccy)}
            subline={fmtPct(summary.total_pnl_pct, 2)}
            tone={pnlGood ? 'good' : 'warn'}
          />
          <Big
            label="Offenes Risiko"
            value={fmtMoneyPlain(summary.total_open_risk, ccy)}
            subline={`${summary.total_open_risk_pct.toFixed(1)} % der Equity`}
          />
        </div>
        <p className="text-[10px] text-zinc-500">
          Alle Kennzahlen (Win-Rate, Profit-Faktor, Drawdown, …) im Depot.
        </p>
      </section>
    );
  }

  return (
    <section className={cn(CARD_PROMINENT, 'space-y-4')}>
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Wallet className="size-4 text-blue-300" />
          <h2 className="text-sm font-semibold text-zinc-100">Paper-Konto</h2>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">
            {summary.open_count} offen · {summary.closed_count} geschlossen
          </span>
        </div>
        <span
          className={cn(
            'rounded border px-2 py-0.5 text-[11px] font-medium',
            ASSESSMENT_PILL[summary.assessment.tone]
          )}
        >
          {summary.assessment.headline}
        </span>
      </header>

      {/* Hauptkennzahlen + Equity-Sparkline */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="sm:col-span-1">
          <Big
            label="Equity"
            value={fmtMoneyPlain(summary.equity, ccy)}
            subline={`Start ${fmtMoneyPlain(summary.account_balance_setting, ccy)}`}
          />
          {/* TT1: Mini-Sparkline aus Closed-Trade-Curve */}
          <EquitySparkline
            curve={summary.equity_curve}
            startBalance={summary.account_balance_setting}
          />
        </div>
        <Big
          label="Gesamt P&L"
          value={fmtMoney(summary.total_pnl, ccy)}
          subline={fmtPct(summary.total_pnl_pct, 2)}
          tone={pnlGood ? 'good' : 'warn'}
        />
        <Big
          label="Investiert"
          value={fmtMoneyPlain(summary.invested_capital, ccy)}
          subline={`${summary.invested_pct.toFixed(0)} % der Equity`}
        />
        <Big
          label="Freies Cash"
          value={fmtMoneyPlain(summary.free_cash, ccy)}
          subline={`${(100 - summary.invested_pct).toFixed(0)} % der Equity`}
        />
      </div>

      {/* Welle 8 / A7 — P&L-Aufteilung + Risk nur zeigen wenn Konto aktiv ist
          (mind. 1 offener oder geschlossener Trade). Bei leerem Konto: weniger Lärm. */}
      {(summary.open_count > 0 || summary.closed_count > 0) && (
        <div className={cn('grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3', SECTION_DIVIDER)}>
          <Stat
            label="Realisiert"
            value={fmtMoney(summary.realized_pnl, ccy)}
            tone={summary.realized_pnl >= 0 ? 'good' : 'warn'}
            subline={
              summary.realized_today !== 0
                ? `Heute ${fmtMoney(summary.realized_today, ccy)}`
                : 'Heute 0'
            }
            icon={summary.realized_pnl >= 0 ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
          />
          <Stat
            label="Unrealisiert"
            value={fmtMoney(summary.unrealized_pnl, ccy)}
            tone={summary.unrealized_pnl >= 0 ? 'good' : 'warn'}
            icon={<Activity className="size-3" />}
          />
          <Stat
            label="Offenes Risiko"
            value={fmtMoneyPlain(summary.total_open_risk, ccy)}
            subline={`${summary.total_open_risk_pct.toFixed(2)} % der Equity`}
            tone={summary.total_open_risk_pct > 6 ? 'warn' : summary.total_open_risk_pct > 3 ? 'neutral' : 'good'}
          />
          <Stat
            label="Drawdown"
            value={summary.current_drawdown_pct === null ? '–' : `${summary.current_drawdown_pct.toFixed(1)} %`}
            subline={summary.max_drawdown_pct === null ? '' : `Max ${summary.max_drawdown_pct.toFixed(1)} %`}
            tone={
              (summary.current_drawdown_pct ?? 0) > 8
                ? 'warn'
                : (summary.current_drawdown_pct ?? 0) > 4
                  ? 'neutral'
                  : 'good'
            }
          />
        </div>
      )}

      {/* Trade-Statistik */}
      {summary.closed_count > 0 && (
        <div className={cn('grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3', SECTION_DIVIDER)}>
          <Stat
            label="Win-Rate"
            value={summary.win_rate === null ? '–' : `${(summary.win_rate * 100).toFixed(0)} %`}
            subline={`${summary.win_count}W / ${summary.loss_count}L`}
          />
          <Stat
            label="Avg R / Trade"
            value={summary.avg_r === null ? '–' : `${summary.avg_r >= 0 ? '+' : ''}${summary.avg_r.toFixed(2)} R`}
            tone={(summary.avg_r ?? 0) > 0 ? 'good' : 'warn'}
          />
          <Stat
            label="Profit-Factor"
            value={summary.profit_factor === null ? '–' : summary.profit_factor.toFixed(2)}
            tone={(summary.profit_factor ?? 0) > 1.5 ? 'good' : (summary.profit_factor ?? 0) >= 1 ? 'neutral' : 'warn'}
          />
          <Stat
            label="Avg Halten"
            value={
              summary.avg_holding_days === null
                ? '–'
                : `${summary.avg_holding_days.toFixed(0)} Tage`
            }
          />
        </div>
      )}

      {/* Konzentration / Größen */}
      {summary.open_count > 0 && (
        <div className={cn('grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3', SECTION_DIVIDER)}>
          <Stat
            label="Größte Position"
            value={fmtMoneyPlain(summary.biggest_position_value, ccy)}
            subline={
              summary.concentration_top1 !== null
                ? `${(summary.concentration_top1 * 100).toFixed(0)} % der Investition`
                : ''
            }
            tone={(summary.concentration_top1 ?? 0) > 0.4 ? 'warn' : 'neutral'}
          />
          {summary.biggest_winner_pnl !== null && summary.biggest_winner_pnl > 0 && (
            <Stat
              label="Best. Gewinner"
              value={fmtMoney(summary.biggest_winner_pnl, ccy)}
              tone="good"
            />
          )}
          {summary.biggest_loser_pnl !== null && summary.biggest_loser_pnl < 0 && (
            <Stat
              label="Schl. Verlierer"
              value={fmtMoney(summary.biggest_loser_pnl, ccy)}
              tone="warn"
            />
          )}
          {summary.avg_winner_native !== null && summary.avg_loser_native !== null && (
            <Stat
              label="Avg W / Avg L"
              value={`${fmtMoney(summary.avg_winner_native, ccy)} / ${fmtMoney(summary.avg_loser_native, ccy)}`}
            />
          )}
        </div>
      )}

      {/* Bewertungs-Notes */}
      {summary.assessment.notes.length > 0 && (
        <ul className={cn('space-y-1 pt-3', SECTION_DIVIDER)}>
          {summary.assessment.notes.map((n, i) => (
            <li key={i} className="text-[11px] text-zinc-400 leading-snug flex items-start gap-1">
              <span className="mt-1 size-1 rounded-full bg-zinc-500 shrink-0" />
              {n}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * TT1 — Schlanke SVG-Sparkline ohne externe Lib. Punkte normalisiert,
 * Farbe je nach finalem Trend.
 */
function EquitySparkline({
  curve,
  startBalance,
}: {
  curve: Array<{ at: string; equity: number }>;
  startBalance: number;
}) {
  if (curve.length < 2) {
    return (
      <div className="mt-1 text-[10px] text-zinc-500">
        Noch keine Trades — Equity-Verlauf erscheint, sobald geschlossene Trades vorliegen.
      </div>
    );
  }
  const W = 120;
  const H = 28;
  const values = curve.map((c) => c.equity);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(0.0001, max - min);
  const lastEquity = values[values.length - 1] ?? 0;
  const trendUp = lastEquity >= startBalance;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * W;
      const y = H - ((v - min) / range) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  // Baseline (Startkapital) als gestrichelte Linie
  const baselineY = H - ((startBalance - min) / range) * H;
  const stroke = trendUp ? 'stroke-emerald-400/80' : 'stroke-red-400/80';
  const fill = trendUp ? 'fill-emerald-500/10' : 'fill-red-500/10';
  const areaPoints = `0,${H} ${points} ${W},${H}`;

  return (
    <div className="mt-1.5">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full max-w-[140px] h-7"
        preserveAspectRatio="none"
      >
        <polygon points={areaPoints} className={fill} />
        <polyline
          points={points}
          fill="none"
          strokeWidth="1.2"
          className={stroke}
        />
        {Number.isFinite(baselineY) && (
          <line
            x1="0"
            x2={W}
            y1={baselineY}
            y2={baselineY}
            strokeWidth="0.5"
            strokeDasharray="2 2"
            className="stroke-zinc-600"
          />
        )}
      </svg>
      <div className="text-[9px] text-zinc-500 mt-0.5">
        Verlauf · {curve.length - 1} Trades
      </div>
    </div>
  );
}

function Big({
  label,
  value,
  subline,
  tone,
}: {
  label: string;
  value: string;
  subline?: string;
  tone?: 'good' | 'warn';
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div
        className={cn(
          'font-mono text-lg font-semibold mt-0.5',
          tone === 'good' ? 'text-emerald-300' : tone === 'warn' ? 'text-red-300' : 'text-zinc-100'
        )}
      >
        {value}
      </div>
      {subline && <div className="text-[10px] text-zinc-500 mt-0.5">{subline}</div>}
    </div>
  );
}

function Stat({
  label,
  value,
  subline,
  tone,
  icon,
}: {
  label: string;
  value: string;
  subline?: string;
  tone?: 'good' | 'warn' | 'neutral';
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div
        className={cn(
          'font-mono text-sm mt-0.5',
          tone === 'good' ? 'text-emerald-300' : tone === 'warn' ? 'text-red-300' : 'text-zinc-100'
        )}
      >
        {value}
      </div>
      {subline && <div className="text-[10px] text-zinc-500 mt-0.5">{subline}</div>}
    </div>
  );
}
